import { Button, Checkbox, DatePicker, Radio, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  EMPTY_SUBSCRIPTION_END_DATE_FILTER,
  SubscriptionEndDateFilterValue,
  SubscriptionEndDatePeriod,
} from '../config';
import {
  isSameSubscriptionEndDateFilter,
  isSubscriptionEndDateFiltered,
  periodRange,
} from '../lib';

import styles from './SubscriptionEndDateFilter.module.scss';

type SubscriptionEndDateFilterProps = {
  open: boolean;
  value: SubscriptionEndDateFilterValue;
  onChange: (value: SubscriptionEndDateFilterValue) => void;
  onClose: () => void;
};

export const SubscriptionEndDateFilter: FC<SubscriptionEndDateFilterProps> = (
  props,
) => {
  const { open, value, onChange, onClose } = props;

  const { formatMessage } = useIntl();

  const [period, setPeriod] = useState<SubscriptionEndDatePeriod>(value.period);
  const [from, setFrom] = useState<Dayjs | undefined>(
    value.from && dayjs(value.from),
  );
  const [to, setTo] = useState<Dayjs | undefined>(value.to && dayjs(value.to));
  const [onlyActive, setOnlyActive] = useState(value.onlyActive);

  // The dropdown stays mounted once it has been opened, so a draft abandoned
  // by clicking outside would still be there the next time it is opened.
  useEffect(() => {
    if (open) {
      setPeriod(value.period);
      setFrom(value.from && dayjs(value.from));
      setTo(value.to && dayjs(value.to));
      setOnlyActive(value.onlyActive);
    }
  }, [open, value]);

  const handleApply = () => {
    const range =
      period === 'custom'
        ? { from: from?.startOf('day'), to: to?.endOf('day') }
        : periodRange(period);

    const applied: SubscriptionEndDateFilterValue = {
      period,
      from: range.from?.toDate(),
      to: range.to?.toDate(),
      onlyActive,
    };

    // Every new value reopens the stream from scratch, so an Apply that moves
    // nothing has to hand back the value that is already in force.
    const next = isSubscriptionEndDateFiltered(applied)
      ? applied
      : EMPTY_SUBSCRIPTION_END_DATE_FILTER;

    onChange(isSameSubscriptionEndDateFilter(next, value) ? value : next);
    onClose();
  };

  const handleReset = () => {
    setPeriod(EMPTY_SUBSCRIPTION_END_DATE_FILTER.period);
    setFrom(undefined);
    setTo(undefined);
    setOnlyActive(EMPTY_SUBSCRIPTION_END_DATE_FILTER.onlyActive);

    onChange(
      isSubscriptionEndDateFiltered(value)
        ? EMPTY_SUBSCRIPTION_END_DATE_FILTER
        : value,
    );
    onClose();
  };

  return (
    <div className={styles.container}>
      <Space direction="vertical" size="middle">
        <Radio.Group
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className={styles.periods}
        >
          <Space direction="vertical" size="small">
            <Radio value="all">
              <FormattedMessage
                id="organizations.subscriptionEndDateFilter.all"
                defaultMessage="All"
              />
            </Radio>
            <Radio value="today">
              <FormattedMessage
                id="organizations.subscriptionEndDateFilter.today"
                defaultMessage="Today"
              />
            </Radio>
            <Radio value="week">
              <FormattedMessage
                id="organizations.subscriptionEndDateFilter.week"
                defaultMessage="One week"
              />
            </Radio>
            <Radio value="custom">
              <FormattedMessage
                id="organizations.subscriptionEndDateFilter.custom"
                defaultMessage="Custom range"
              />
            </Radio>
          </Space>
        </Radio.Group>

        {period === 'custom' && (
          <Space direction="vertical" size="small">
            <DatePicker
              value={from ?? null}
              onChange={(date) => setFrom(date ?? undefined)}
              disabledDate={(date) => !!to && date.isAfter(to, 'day')}
              placeholder={formatMessage({
                id: 'organizations.subscriptionEndDateFilter.from',
                defaultMessage: 'From',
              })}
            />
            <DatePicker
              value={to ?? null}
              onChange={(date) => setTo(date ?? undefined)}
              disabledDate={(date) => !!from && date.isBefore(from, 'day')}
              placeholder={formatMessage({
                id: 'organizations.subscriptionEndDateFilter.to',
                defaultMessage: 'To',
              })}
            />
          </Space>
        )}

        <Checkbox
          checked={onlyActive}
          onChange={(e) => setOnlyActive(e.target.checked)}
        >
          <FormattedMessage
            id="organizations.subscriptionEndDateFilter.onlyActive"
            defaultMessage="Only active"
          />
        </Checkbox>

        <div className={styles.actions}>
          <Button type="link" size="small" onClick={handleReset}>
            <FormattedMessage id="global.reset" defaultMessage="Reset" />
          </Button>
          <Button type="primary" size="small" onClick={handleApply}>
            <FormattedMessage id="global.apply" defaultMessage="Apply" />
          </Button>
        </div>
      </Space>
    </div>
  );
};
